import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { OutlineButton, PrimaryButton, StatusBadge } from '../../../components/ui';
import { lookupAppUserGst } from '../../../repositories/appUser.repository';

function ResultRow({ label, value }) {
  if (value == null || value === '') return null;
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  return (
    <Box
      px={3}
      py={2}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text fontSize="11px" color="text.secondary" mb={0.5} fontWeight="500">
        {label}
      </Text>
      <Text fontSize="14px" fontWeight="600" whiteSpace="pre-wrap" lineHeight="1.35">
        {display}
      </Text>
    </Box>
  );
}

function GstResultGrid({ result }) {
  if (!result) return null;
  const addressDetails = result.addressDetails || null;
  return (
    <VStack align="stretch" spacing={3}>
      <Flex align="center" gap={2}>
        <Heading size="xs">Result</Heading>
        {result.saved || result.id ? (
          <StatusBadge status="verified">Saved</StatusBadge>
        ) : null}
      </Flex>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
        <ResultRow label="GSTIN" value={result.gstin} />
        <ResultRow label="Status" value={result.status} />
        <ResultRow label="Legal name" value={result.legalName} />
        <ResultRow label="Trade name" value={result.tradeName} />
        <ResultRow label="Taxpayer type" value={result.taxpayerType} />
        <ResultRow label="Constitution" value={result.businessConstitution} />
        <ResultRow label="Registration date" value={result.registrationDate} />
        <ResultRow label="Cancellation date" value={result.cancellationDate} />
        <ResultRow label="State code" value={result.stateCode} />
        <ResultRow label="State jurisdiction" value={result.stateJurisdiction} />
        <ResultRow label="City" value={result.city} />
        <ResultRow label="PIN" value={result.pincode} />
        <ResultRow label="Block status" value={result.blockStatus} />
      </SimpleGrid>
      <ResultRow label="Address" value={result.address} />
      <ResultRow label="Nature of business" value={result.natureOfBusiness} />

      {addressDetails && typeof addressDetails === 'object' ? (
        <Box>
          <Text fontSize="12px" color="text.secondary" mb={1} fontWeight="600">
            Address details
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
            <ResultRow label="Building number" value={addressDetails.building_number} />
            <ResultRow label="Building name" value={addressDetails.building_name} />
            <ResultRow label="Floor" value={addressDetails.floor} />
            <ResultRow label="Street" value={addressDetails.street} />
            <ResultRow label="Locality" value={addressDetails.locality} />
            <ResultRow label="Landmark" value={addressDetails.landmark} />
            <ResultRow label="District" value={addressDetails.district} />
            <ResultRow label="City" value={addressDetails.city} />
            <ResultRow label="State" value={addressDetails.state} />
            <ResultRow label="PIN" value={addressDetails.pincode} />
          </SimpleGrid>
        </Box>
      ) : null}
    </VStack>
  );
}

export function GstLookupModal({ isOpen, onClose, user, onSaved }) {
  const [gstin, setGstin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const existing = user?.gstDetails;
    setGstin(
      String(existing?.gstin || user?.gstNumber || '')
        .trim()
        .toUpperCase()
    );
    setError('');
    setResult(existing || null);
    setLoading(false);
  }, [isOpen, user?.gstNumber, user?.gstDetails, user?.id]);

  const run = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await lookupAppUserGst(user.id, gstin.trim() || undefined);
      setResult(data);
      if (typeof onSaved === 'function') {
        await onSaved();
      }
    } catch (e) {
      setError(e?.message || 'GST lookup failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setResult(null);
    setLoading(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <MdSearch />
            Fetch GST details
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="text.secondary" mb={3}>
            Looks up GSTIN via the server and saves the result on this member
            (one record per member; re-fetch updates it).
          </Text>

          <FormControl mb={3}>
            <FormLabel fontSize="13px">GSTIN</FormLabel>
            <Input
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              placeholder="15-character GSTIN"
              maxLength={15}
              fontFamily="mono"
              isDisabled={loading}
            />
          </FormControl>

          <PrimaryButton
            size="sm"
            leftIcon={<MdSearch />}
            onClick={run}
            isLoading={loading}
            mb={4}
          >
            Fetch &amp; save
          </PrimaryButton>

          {loading ? (
            <Flex align="center" gap={3} py={6} justify="center">
              <Spinner />
              <Text fontSize="14px">Fetching GST details…</Text>
            </Flex>
          ) : null}

          {error ? (
            <Box
              p={3}
              borderRadius="md"
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
              mb={3}
            >
              <Text fontSize="14px" color="red.700" fontWeight="600">
                {error}
              </Text>
            </Box>
          ) : null}

          {!loading ? <GstResultGrid result={result} /> : null}
        </ModalBody>
        <ModalFooter>
          <OutlineButton onClick={handleClose}>Close</OutlineButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
